// Content Hub Type Definitions

// Supported Social Platforms
export type SocialPlatform =
  | 'instagram'
  | 'facebook'
  | 'linkedin'
  | 'twitter'
  | 'youtube'
  | 'youtube_shorts'
  | 'tiktok'
  | 'threads';

// Content Formats per Platform
export type ContentFormat =
  | 'post'
  | 'story'
  | 'reel'
  | 'carousel'
  | 'video'
  | 'short'
  | 'article'
  | 'thread';

// Content Status
export type ContentStatus =
  | 'draft'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'archived';

// Media Types
export type MediaType = 'image' | 'video' | 'gif';

// Platform Configuration
export interface PlatformConfig {
  id: SocialPlatform;
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  supportedFormats: ContentFormat[];
  maxCharacters: number;
  maxHashtags: number;
  maxMentions?: number;
  mediaConstraints: MediaConstraints;
}

export interface MediaConstraints {
  maxImages: number;
  maxVideoDuration: number; // in seconds
  supportedImageFormats: string[];
  supportedVideoFormats: string[];
  aspectRatios: AspectRatio[];
  maxFileSize: number; // in MB
}

export interface AspectRatio {
  name: string;
  value: string;
  width: number;
  height: number;
}

// Platform Configurations Map
export const PLATFORM_CONFIGS: Record<SocialPlatform, PlatformConfig> = {
  instagram: {
    id: 'instagram',
    name: 'Instagram',
    icon: 'instagram',
    color: '#E4405F',
    bgColor: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400',
    supportedFormats: ['post', 'story', 'reel', 'carousel'],
    maxCharacters: 2200,
    maxHashtags: 30,
    maxMentions: 20,
    mediaConstraints: {
      maxImages: 10,
      maxVideoDuration: 900,
      supportedImageFormats: ['jpg', 'jpeg', 'png'],
      supportedVideoFormats: ['mp4', 'mov'],
      aspectRatios: [
        { name: 'Square', value: '1:1', width: 1080, height: 1080 },
        { name: 'Portrait', value: '4:5', width: 1080, height: 1350 },
        { name: 'Landscape', value: '1.91:1', width: 1080, height: 566 },
        { name: 'Story/Reel', value: '9:16', width: 1080, height: 1920 }
      ],
      maxFileSize: 100
    }
  },
  facebook: {
    id: 'facebook',
    name: 'Facebook',
    icon: 'facebook',
    color: '#1877F2',
    bgColor: 'bg-blue-600',
    supportedFormats: ['post', 'story', 'reel', 'video'],
    maxCharacters: 63206,
    maxHashtags: 30,
    mediaConstraints: {
      maxImages: 10,
      maxVideoDuration: 14400, // 240 minutes
      supportedImageFormats: ['jpg', 'jpeg', 'png', 'gif'],
      supportedVideoFormats: ['mp4', 'mov', 'avi'],
      aspectRatios: [
        { name: 'Landscape', value: '16:9', width: 1200, height: 675 },
        { name: 'Square', value: '1:1', width: 1200, height: 1200 },
        { name: 'Portrait', value: '4:5', width: 1080, height: 1350 }
      ],
      maxFileSize: 4096
    }
  },
  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: 'linkedin',
    color: '#0A66C2',
    bgColor: 'bg-blue-700',
    supportedFormats: ['post', 'article', 'video', 'carousel'],
    maxCharacters: 3000,
    maxHashtags: 5,
    mediaConstraints: {
      maxImages: 9,
      maxVideoDuration: 600, // 10 minutes
      supportedImageFormats: ['jpg', 'jpeg', 'png', 'gif'],
      supportedVideoFormats: ['mp4', 'mov'],
      aspectRatios: [
        { name: 'Landscape', value: '1.91:1', width: 1200, height: 628 },
        { name: 'Square', value: '1:1', width: 1200, height: 1200 },
        { name: 'Portrait', value: '4:5', width: 1080, height: 1350 }
      ],
      maxFileSize: 200
    }
  },
  twitter: {
    id: 'twitter',
    name: 'X (Twitter)',
    icon: 'twitter',
    color: '#000000',
    bgColor: 'bg-black',
    supportedFormats: ['post', 'thread', 'video'],
    maxCharacters: 280,
    maxHashtags: 10,
    mediaConstraints: {
      maxImages: 4,
      maxVideoDuration: 140,
      supportedImageFormats: ['jpg', 'jpeg', 'png', 'gif'],
      supportedVideoFormats: ['mp4', 'mov'],
      aspectRatios: [
        { name: 'Landscape', value: '16:9', width: 1200, height: 675 },
        { name: 'Square', value: '1:1', width: 1200, height: 1200 }
      ],
      maxFileSize: 512
    }
  },
  youtube: {
    id: 'youtube',
    name: 'YouTube',
    icon: 'youtube',
    color: '#FF0000',
    bgColor: 'bg-red-600',
    supportedFormats: ['video'],
    maxCharacters: 5000,
    maxHashtags: 15,
    mediaConstraints: {
      maxImages: 1,
      maxVideoDuration: 43200, // 12 hours
      supportedImageFormats: ['jpg', 'jpeg', 'png'],
      supportedVideoFormats: ['mp4', 'mov', 'avi', 'wmv', 'flv', 'webm'],
      aspectRatios: [
        { name: 'Widescreen', value: '16:9', width: 1920, height: 1080 }
      ],
      maxFileSize: 256000 // 256GB
    }
  },
  youtube_shorts: {
    id: 'youtube_shorts',
    name: 'YouTube Shorts',
    icon: 'youtube',
    color: '#FF0000',
    bgColor: 'bg-red-600',
    supportedFormats: ['short'],
    maxCharacters: 100,
    maxHashtags: 3,
    mediaConstraints: {
      maxImages: 0,
      maxVideoDuration: 60,
      supportedImageFormats: [],
      supportedVideoFormats: ['mp4', 'mov'],
      aspectRatios: [
        { name: 'Vertical', value: '9:16', width: 1080, height: 1920 }
      ],
      maxFileSize: 256000
    }
  },
  tiktok: {
    id: 'tiktok',
    name: 'TikTok',
    icon: 'tiktok',
    color: '#000000',
    bgColor: 'bg-black',
    supportedFormats: ['video'],
    maxCharacters: 2200,
    maxHashtags: 100,
    mediaConstraints: {
      maxImages: 0,
      maxVideoDuration: 600, // 10 minutes
      supportedImageFormats: [],
      supportedVideoFormats: ['mp4', 'mov'],
      aspectRatios: [
        { name: 'Vertical', value: '9:16', width: 1080, height: 1920 }
      ],
      maxFileSize: 4096
    }
  },
  threads: {
    id: 'threads',
    name: 'Threads',
    icon: 'threads',
    color: '#000000',
    bgColor: 'bg-black',
    supportedFormats: ['post', 'thread'],
    maxCharacters: 500,
    maxHashtags: 5,
    mediaConstraints: {
      maxImages: 10,
      maxVideoDuration: 300, // 5 minutes
      supportedImageFormats: ['jpg', 'jpeg', 'png', 'gif'],
      supportedVideoFormats: ['mp4', 'mov'],
      aspectRatios: [
        { name: 'Any', value: 'any', width: 0, height: 0 }
      ],
      maxFileSize: 100
    }
  }
};

// Content Creation State
export interface ContentCreationState {
  step: number;
  selectedPlatforms: SocialPlatform[];
  contentFormat: ContentFormat;
  mode: 'generate' | 'upload';
  content: ContentData;
  scheduling: SchedulingOptions;
  platformOverrides: Partial<Record<SocialPlatform, PlatformContentOverride>>;
}

export interface ContentData {
  text: string;
  media: MediaFile[];
  hashtags: string[];
  mentions: string[];
  link?: string;
  linkPreview?: LinkPreview;
  // For AI generation
  aiPrompt?: string;
  aiTone?: ContentTone;
  aiTargetAudience?: string;
}

export interface PlatformContentOverride {
  text?: string;
  hashtags?: string[];
  media?: MediaFile[];
}

export interface MediaFile {
  id: string;
  type: MediaType;
  url: string;
  thumbnailUrl?: string;
  fileName: string;
  fileSize: number;
  width?: number;
  height?: number;
  duration?: number; // for videos, in seconds
  aspectRatio?: string;
  uploadProgress?: number;
  status: 'uploading' | 'processing' | 'ready' | 'error';
  errorMessage?: string;
}

export interface LinkPreview {
  url: string;
  title: string;
  description: string;
  image?: string;
  domain: string;
}

// Content Tones for AI Generation
export type ContentTone =
  | 'professional'
  | 'casual'
  | 'friendly'
  | 'humorous'
  | 'inspirational'
  | 'educational'
  | 'promotional'
  | 'storytelling';

export const CONTENT_TONES: { id: ContentTone; label: string; description: string }[] = [
  { id: 'professional', label: 'Professional', description: 'Formal and business-appropriate' },
  { id: 'casual', label: 'Casual', description: 'Relaxed and conversational' },
  { id: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
  { id: 'humorous', label: 'Humorous', description: 'Light-hearted and fun' },
  { id: 'inspirational', label: 'Inspirational', description: 'Motivating and uplifting' },
  { id: 'educational', label: 'Educational', description: 'Informative and teaching' },
  { id: 'promotional', label: 'Promotional', description: 'Sales-focused and persuasive' },
  { id: 'storytelling', label: 'Storytelling', description: 'Narrative and engaging' }
];

// Scheduling Options
export interface SchedulingOptions {
  publishNow: boolean;
  scheduledDate?: Date;
  scheduledTime?: string;
  timezone: string;
  repeatEnabled: boolean;
  repeatFrequency?: 'daily' | 'weekly' | 'monthly';
  repeatEndDate?: Date;
}

// Social Account Connection
export interface SocialAccount {
  id: string;
  platform: SocialPlatform;
  accountName: string;
  accountHandle: string;
  profileImage?: string;
  isConnected: boolean;
  connectionStatus: 'connected' | 'expired' | 'error' | 'pending';
  lastSynced?: Date;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: Date;
  permissions: string[];
  metadata?: Record<string, any>;
}

// Content Item (for dashboard/list)
export interface ContentItem {
  id: string;
  projectId: string;
  content: ContentData;
  platforms: SocialPlatform[];
  format: ContentFormat;
  status: ContentStatus;
  createdAt: Date;
  updatedAt: Date;
  scheduledFor?: Date;
  publishedAt?: Date;
  platformResults: PlatformPublishResult[];
  analytics?: ContentAnalytics;
  tags: string[];
  createdBy: string;
}

export interface PlatformPublishResult {
  platform: SocialPlatform;
  accountId: string;
  status: 'pending' | 'success' | 'failed';
  postId?: string;
  postUrl?: string;
  errorMessage?: string;
  publishedAt?: Date;
}

// Analytics
export interface ContentAnalytics {
  totalReach: number;
  totalImpressions: number;
  totalEngagement: number;
  platformMetrics: Record<SocialPlatform, PlatformMetrics>;
}

export interface PlatformMetrics {
  reach: number;
  impressions: number;
  likes: number;
  comments: number;
  shares: number;
  saves?: number;
  clicks?: number;
  videoViews?: number;
  avgWatchTime?: number;
}

// Calendar Event
export interface CalendarEvent {
  id: string;
  contentId: string;
  title: string;
  platforms: SocialPlatform[];
  scheduledFor: Date;
  status: ContentStatus;
  thumbnailUrl?: string;
}

// Dashboard Stats
export interface ContentDashboardStats {
  totalPosts: number;
  publishedThisWeek: number;
  scheduledCount: number;
  draftsCount: number;
  platformBreakdown: Record<SocialPlatform, number>;
  topPerformingPosts: ContentItem[];
  recentActivity: ActivityItem[];
}

export interface ActivityItem {
  id: string;
  type: 'created' | 'published' | 'scheduled' | 'failed';
  contentId: string;
  contentPreview: string;
  platforms: SocialPlatform[];
  timestamp: Date;
}

// API Response Types
export interface ContentListResponse {
  items: ContentItem[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface PublishResponse {
  success: boolean;
  contentId: string;
  results: PlatformPublishResult[];
  failedPlatforms: SocialPlatform[];
}

// Form Types
export interface ContentFormData {
  text: string;
  platforms: SocialPlatform[];
  format: ContentFormat;
  media: File[];
  hashtags: string;
  link?: string;
  scheduledDate?: string;
  scheduledTime?: string;
}

// Filter & Sort Options
export interface ContentFilters {
  status?: ContentStatus[];
  platforms?: SocialPlatform[];
  dateRange?: { start: Date; end: Date };
  tags?: string[];
  search?: string;
}

export type ContentSortBy = 'createdAt' | 'scheduledFor' | 'publishedAt' | 'engagement';
export type SortOrder = 'asc' | 'desc';

// Helper function to get platform config
export function getPlatformConfig(platform: SocialPlatform): PlatformConfig {
  return PLATFORM_CONFIGS[platform];
}

// Helper to validate content against platform constraints
export function validateContentForPlatform(
  content: ContentData,
  platform: SocialPlatform
): { valid: boolean; errors: string[] } {
  const config = PLATFORM_CONFIGS[platform];
  const errors: string[] = [];

  // Check character limit
  if (content.text.length > config.maxCharacters) {
    errors.push(`Text exceeds ${config.maxCharacters} character limit for ${config.name}`);
  }

  // Check hashtag limit
  if (content.hashtags.length > config.maxHashtags) {
    errors.push(`Too many hashtags (max ${config.maxHashtags} for ${config.name})`);
  }

  // Check media limits
  const imageCount = content.media.filter(m => m.type === 'image').length;
  if (imageCount > config.mediaConstraints.maxImages) {
    errors.push(`Too many images (max ${config.mediaConstraints.maxImages} for ${config.name})`);
  }

  // Check video duration
  const videos = content.media.filter(m => m.type === 'video');
  for (const video of videos) {
    if (video.duration && video.duration > config.mediaConstraints.maxVideoDuration) {
      errors.push(`Video exceeds ${config.mediaConstraints.maxVideoDuration}s limit for ${config.name}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

// Helper to get character count with warning levels
export function getCharacterCountStatus(
  text: string,
  platform: SocialPlatform
): { count: number; max: number; status: 'ok' | 'warning' | 'error' } {
  const config = PLATFORM_CONFIGS[platform];
  const count = text.length;
  const max = config.maxCharacters;

  if (count > max) return { count, max, status: 'error' };
  if (count > max * 0.9) return { count, max, status: 'warning' };
  return { count, max, status: 'ok' };
}
